import type { Tag } from "@engram/core";
import { ChevronDown, ChevronRight, MoreHorizontal, Plus, Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { addShortcut, createTag, deleteTag, renameTag, tagNameError } from "../db/actions";
import { db, useQuery } from "../db/client";
import { collator, groupKey } from "./format";
import { Confirm, Menu, Prompt, useAnchor } from "./Menu";
import { usePrefs } from "./prefs";

type Row = Tag & { n: number };

/** Evernote's Tags flyout: grouped by initial (A–Z, Hangul initials), with counts. */
export function TagsPanel({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overTop, setOverTop] = useState(false);
  const [, navigate] = useLocation();
  const [path] = useLocation();
  const rows = useQuery<Row>(
    // Every tag counted in one pass over notes (a count per tag re-read every note's tags once per tag).
    `SELECT t.*, COALESCE(c.n, 0) AS n FROM tags t LEFT JOIN (SELECT je.value AS tag_id, COUNT(*) AS n FROM notes n, json_each(n.tag_ids) je WHERE n.deleted = 0 AND n.trashed_at IS NULL GROUP BY je.value) c ON c.tag_id = t.id WHERE t.deleted = 0`,
    [],
    ["tags", "notes"],
  );
  const all = (rows ?? []).sort((a, b) => collator.compare(a.name, b.name));
  const panel = useRef<HTMLElement>(null);

  // Evernote's flyout closes on Escape or a click outside it (menus and dialogs it opened don't count).
  useEffect(() => {
    const busy = () => !!document.querySelector(".popover, .modal-backdrop");
    const key = (e: KeyboardEvent) => e.key === "Escape" && !busy() && onClose();
    const down = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (panel.current?.contains(t) || busy() || t.closest(".nav-tags, .rail-tags")) return;
      onClose();
    };
    document.addEventListener("keydown", key);
    document.addEventListener("mousedown", down);
    return () => {
      document.removeEventListener("keydown", key);
      document.removeEventListener("mousedown", down);
    };
  }, [onClose]);
  const filtered = q ? all.filter((t) => t.name.toLowerCase().includes(q.toLowerCase())) : all.filter((t) => !t.parent_id || !all.some((p) => p.id === t.parent_id));
  const groups = new Map<string, Row[]>();
  for (const t of filtered) {
    const k = groupKey(t.name);
    groups.set(k, [...(groups.get(k) ?? []), t]);
  }

  return (
    <aside className="tags-panel" ref={panel}>
      <header className="tags-header">
        <h2>
          Tags <span className="title-count">{rows ? rows.length : ""}</span>
        </h2>
        <button className="icon-btn" title="New tag" onClick={() => setCreating(true)}><Plus size={16} className="ic-create" /></button>
        <button className="icon-btn" title="Close" onClick={onClose}><X size={16} /></button>
      </header>
      <label className="find-input">
        <Search size={14} />
        <input autoFocus placeholder="Find tags…" value={q} onChange={(e) => setQ(e.target.value)} />
      </label>
      <div
        className={`tags-scroll${overTop ? " drop-top" : ""}`}
        onDragOver={(e) => {
          // Dropping on empty space (not on a tag) moves the tag to the top level.
          if (!dragId || (e.target as HTMLElement).closest(".tag-row")) return setOverTop(false);
          e.preventDefault();
          setOverTop(true);
        }}
        onDragLeave={() => setOverTop(false)}
        onDrop={(e) => {
          setOverTop(false);
          if (!dragId || (e.target as HTMLElement).closest(".tag-row")) return;
          e.preventDefault();
          void nestTag(dragId, null);
        }}
      >
        {dragId && <div className="tags-drop-hint">Drop on a tag to nest it, or here for the top level</div>}
        {[...groups].map(([k, tags]) => (
          <section key={k}>
            <div className="tags-group">{k}</div>
            {tags.map((t) => (
              <TagRow key={t.id} tag={t} all={all} depth={0} path={path} flat={!!q} onOpen={(id) => navigate(`/tag/${id}`)} drag={{ id: dragId, set: setDragId }} />
            ))}
          </section>
        ))}
        {rows && all.length === 0 && <p className="muted pad">No tags yet. Add tags from the bar at the bottom of a note.</p>}
      </div>
      {creating && <Prompt title="Create new tag" label="Name" confirm="Create" validate={tagNameError()} onSubmit={(v) => void createTag(v)} onClose={() => setCreating(false)} />}
    </aside>
  );
}

function isDescendant(all: Row[], id: string, ancestor: string): boolean {
  for (let t = all.find((x) => x.id === id); t?.parent_id; t = all.find((x) => x.id === t!.parent_id)) {
    if (t.parent_id === ancestor) return true;
  }
  return false;
}

function nestTag(id: string, parentId: string | null) {
  return db.mutate([{ t: "tags", id, set: { parent_id: parentId, updated: Date.now() } }]);
}

function TagRow({ tag, all, depth, path, flat, onOpen, drag }: { tag: Row; all: Row[]; depth: number; path: string; flat: boolean; onOpen: (id: string) => void; drag: { id: string | null; set: (id: string | null) => void } }) {
  const children = flat ? [] : all.filter((t) => t.parent_id === tag.id);
  const { expanded, toggle } = usePrefs();
  const open = !!expanded[`tag:${tag.id}`];
  const [over, setOver] = useState(false);
  const m = useAnchor();
  const [dialog, setDialog] = useState<null | "rename" | "delete" | "nest">(null);
  const active = path === `/tag/${tag.id}`;
  // Evernote: drag a tag onto another to nest it. Not onto itself or its own descendants.
  const canDrop = !!drag.id && drag.id !== tag.id && !isDescendant(all, tag.id, drag.id) && all.find((t) => t.id === drag.id)?.parent_id !== tag.id;
  return (
    <>
      <div
        className={`tag-row${active ? " active" : ""}${over ? " drop-into" : ""}${drag.id === tag.id ? " dragging" : ""}`}
        style={{ paddingLeft: 8 + depth * 16 }}
        draggable
        onDragStart={(e) => {
          drag.set(tag.id);
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", tag.name);
        }}
        onDragEnd={() => drag.set(null)}
        onDragOver={(e) => {
          if (!canDrop) return;
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          setOver(false);
          if (!canDrop || !drag.id) return;
          e.preventDefault();
          e.stopPropagation();
          void nestTag(drag.id, tag.id);
          if (!open) toggle(`tag:${tag.id}`);
        }}
      >
        <button className="expander" onClick={() => toggle(`tag:${tag.id}`)} style={{ visibility: children.length ? "visible" : "hidden" }}>
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </button>
        <button className="tag-name" onClick={() => onOpen(tag.id)}>{tag.name}</button>
        <span className="tag-count">{tag.n}</span>
        <button className="icon-btn row-more" onClick={m.open} aria-label="Tag actions"><MoreHorizontal size={14} /></button>
      </div>
      {open && children.map((c) => <TagRow key={c.id} tag={c} all={all} depth={depth + 1} path={path} flat={flat} onOpen={onOpen} drag={drag} />)}
      {m.anchor && (
        <Menu
          anchor={m.anchor}
          align="end"
          onClose={m.close}
          entries={[
            { label: "Add to Shortcuts", onSelect: () => void addShortcut("tag", tag.id) },
            { label: "Rename tag", onSelect: () => setDialog("rename") },
            { label: "Nest tag in…", onSelect: () => setDialog("nest") },
            ...(tag.parent_id ? [{ label: "Move to top level", onSelect: () => void nestTag(tag.id, null) }] : []),
            { separator: true },
            { label: "Delete tag", danger: true, onSelect: () => setDialog("delete") },
          ]}
        />
      )}
      {dialog === "rename" && <Prompt title="Rename tag" initial={tag.name} validate={tagNameError(tag.id)} onSubmit={(v) => void renameTag(tag.id, v)} onClose={() => setDialog(null)} />}
      {dialog === "nest" && (
        <Prompt
          title="Nest tag"
          label="Parent tag name"
          confirm="Nest"
          onSubmit={async (v) => {
            const parent = all.find((t) => t.name.toLowerCase() === v.toLowerCase());
            const pid = parent?.id ?? (await createTag(v));
            if (pid !== tag.id && !isDescendant(all, pid, tag.id)) await nestTag(tag.id, pid);
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "delete" && (
        <Confirm title="Delete tag?" body={`"${tag.name}" will be removed from ${tag.n} note${tag.n === 1 ? "" : "s"}.`} confirm="Delete" danger onConfirm={() => void deleteTag(tag.id)} onClose={() => setDialog(null)} />
      )}
    </>
  );
}
